import {
  AddOnRateType,
  BillingLineKind,
  InvoiceLineItemType,
} from '@treadinc/horizon-api-spec';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { LoadBasic } from '~hooks/useLoad/models';
import { SettlementInvoiceLines } from '~hooks/useSettlementLines/models';
import isEnumValue from '~utils/isEnumValue';

export interface SettlementSheetCharge {
  addOnCharges: AddOnCharge[];
  invoice: Invoice;
  key: string;
  lineItems: InvoiceLineItem[];
  load: LoadBasic | null;
}

export interface SettlementSheetOrderGroup {
  charges: SettlementSheetCharge[];
  invoice: Invoice;
  jobAddOnCharges: AddOnCharge[];
  key: string;
}

export interface SettlementSheetLines {
  invoiceByLineId: Map<string, Invoice>;
  orderGroups: SettlementSheetOrderGroup[];
}

const isFreightByLineItemType = {
  [InvoiceLineItemType.FREIGHT_RATE_LINE_ITEM]: true,
  [InvoiceLineItemType.INVOICE_LINE_ITEM]: true,
  [InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM]: false,
} satisfies Record<InvoiceLineItemType, boolean>;

export const isFreightLineItem = (lineItem: InvoiceLineItem) =>
  isEnumValue(InvoiceLineItemType, lineItem.lineItemType) &&
  isFreightByLineItemType[lineItem.lineItemType];

interface SettlementSheetShownLineItem {
  lineItem: InvoiceLineItem;
  rolledUpLineItems: InvoiceLineItem[];
}

export function getShownLineItems(
  charge: SettlementSheetCharge,
  isEditing: boolean,
): SettlementSheetShownLineItem[] {
  const freightLineItems = charge.lineItems.filter(isFreightLineItem);
  const materialLineItems = charge.lineItems.filter(
    (lineItem) => !isFreightLineItem(lineItem),
  );
  const rollsUpMaterial =
    !isEditing &&
    charge.invoice.materialRateItemized === false &&
    freightLineItems.length === 1 &&
    materialLineItems.length > 0;

  if (rollsUpMaterial) {
    return [{ lineItem: freightLineItems[0], rolledUpLineItems: materialLineItems }];
  }

  return charge.lineItems.map((lineItem) => ({ lineItem, rolledUpLineItems: [] }));
}

export const isPercentageRate = (rateType: AddOnRateType, percentage: number) =>
  rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
  (rateType === AddOnRateType.FUEL_SURCHARGE && percentage !== 0);

export const isPercentageAddOnCharge = (addOnCharge: AddOnCharge) =>
  isPercentageRate(addOnCharge.revenueCostRateType, addOnCharge.percentage);

export const getAddOnChargeRate = (addOnCharge: AddOnCharge) =>
  isPercentageAddOnCharge(addOnCharge) ? addOnCharge.percentage : addOnCharge.rate;

export const getChargeSettlementLines = ({
  addOnCharges,
  lineItems,
}: SettlementSheetCharge) => [
  ...lineItems.map(({ id }) => ({ id, kind: BillingLineKind.INVOICE_LINE_ITEM })),
  ...addOnCharges.flatMap(({ id }) =>
    id ? [{ id, kind: BillingLineKind.ADD_ON_CHARGE }] : [],
  ),
];

export const countInvoiceLines = (invoiceLines: SettlementInvoiceLines[]) =>
  invoiceLines.reduce((count, { lineCount }) => count + lineCount, 0);
